import type { Dispatch, FC, SetStateAction } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PaperProvider, Switch, Text } from 'react-native-paper';
import { DatePickerModal, TimePickerModal } from 'react-native-paper-dates';
import type { CalendarDate } from 'react-native-paper-dates/lib/typescript/Date/Calendar';

import { ListItem } from '@/features/list/components/ListItem';
import { ListSection } from '@/features/list/components/ListSection';
import {
  DATE_FORMAT,
  TIME_FORMAT
} from '@/features/post-editor/constants/dateTime';
import { useAppSettingStore } from '@/features/profile-setting/store/useAppSettingStore';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import { dayjs, TODAY } from '@/time';

// Todo: use FormDatePicker once we switch to forms for post create
type DateTimePickerProps = {
  startDate: CalendarDate;
  endDate: CalendarDate;
  allDay: boolean;
  setStartDate: Dispatch<SetStateAction<CalendarDate>>;
  setEndDate: Dispatch<SetStateAction<CalendarDate>>;
  setAllDay: (allDay: boolean) => void;
  maximumDurationDays?: number;
};

export const DateTimePicker: FC<DateTimePickerProps> = ({
  startDate,
  endDate,
  allDay,
  setStartDate,
  setEndDate,
  setAllDay,
  maximumDurationDays
}) => {
  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.POST_EDITOR, {
    keyPrefix: 'screens.post-editor'
  });
  const locale = useAppSettingStore((s) => s.selectedLocale);

  const datetimePickerTheme = {
    ...theme,
    colors: {
      ...theme.colors,
      secondaryContainer: theme.colors.primary
    }
  };

  const [isAllDay, setIsAllDay] = useState<boolean>(allDay);

  const handleChange = useCallback(
    (enabled: boolean) => {
      setIsAllDay(enabled);
      setAllDay(enabled);
    },
    [setAllDay]
  );

  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
  const [startTimePickerOpen, setStartTimePickerOpen] = useState(false);
  const [endTimePickerOpen, setEndTimePickerOpen] = useState(false);

  const [startTime, setStartTime] = useState<{
    hours: number;
    minutes: number;
  }>({
    hours: startDate?.getHours() ?? 8,
    minutes: startDate?.getMinutes() ?? 0
  });

  const [endTime, setEndTime] = useState<{
    hours: number;
    minutes: number;
  }>({
    hours: endDate?.getHours() ?? 8,
    minutes: endDate?.getMinutes() ?? 0
  });

  const onStartDateDismiss = useCallback(
    () => setStartDatePickerOpen(false),
    []
  );
  const onEndDateDismiss = useCallback(() => setEndDatePickerOpen(false), []);
  const onStartDateConfirm = useCallback(
    (selected?: { date?: Date }) => {
      setStartDatePickerOpen(false);
      if (!selected?.date) return;

      selected.date?.setHours(startTime.hours);
      selected.date?.setMinutes(startTime.minutes);
      setStartDate(selected.date);

      if (!(endDate && selected.date)) return;

      if (endDate <= selected.date) {
        setEndDate(new Date(selected.date));
      } else if (
        maximumDurationDays &&
        dayjs(endDate).diff(dayjs(selected.date), 'days') > maximumDurationDays
      ) {
        const newEndDate = new Date(selected.date);
        newEndDate.setDate(newEndDate.getDate() + maximumDurationDays);
        setEndDate(newEndDate);
      }
    },
    [endDate, maximumDurationDays, setEndDate, setStartDate, startTime]
  );
  const onEndDateConfirm = useCallback(
    (selected?: { date?: Date }) => {
      setEndDatePickerOpen(false);
      if (!selected?.date) return;

      selected.date?.setHours(endTime.hours);
      selected.date?.setMinutes(endTime.minutes);
      setEndDate(selected.date);
    },
    [setEndDate, endTime]
  );

  const onStartTimeDismiss = useCallback(
    () => setStartTimePickerOpen(false),
    []
  );
  const onEndTimeDismiss = useCallback(() => setEndTimePickerOpen(false), []);
  const onStartTimeConfirm = useCallback(
    ({ hours, minutes }: { hours: number; minutes: number }) => {
      setStartTimePickerOpen(false);
      setStartTime({ hours, minutes });
      const newStartDate = new Date(startDate ?? new Date());
      newStartDate.setHours(hours);
      newStartDate.setMinutes(minutes);
      newStartDate.setSeconds(0);
      newStartDate.setMilliseconds(0);
      setStartDate(newStartDate);
    },
    [setStartDate, startDate]
  );

  const onEndTimeConfirm = useCallback(
    ({ hours, minutes }: { hours: number; minutes: number }) => {
      setEndTimePickerOpen(false);
      setEndTime({ hours, minutes });
      const newEndDate = new Date(endDate ?? new Date());
      newEndDate.setHours(hours);
      newEndDate.setMinutes(minutes);
      newEndDate.setSeconds(0);
      newEndDate.setMilliseconds(0);
      setEndDate(newEndDate);
    },
    [endDate, setEndDate]
  );

  const maxEndDate = useMemo(() => {
    if (!startDate || !maximumDurationDays) return;
    const newMaxEndDate = new Date(startDate);
    newMaxEndDate.setDate(newMaxEndDate.getDate() + maximumDurationDays);
    return newMaxEndDate;
  }, [maximumDurationDays, startDate]);

  return (
    <>
      <ListItem
        title={t('allDay')}
        arrowIcon={() => (
          <Switch
            value={isAllDay}
            onValueChange={handleChange}
            // Todo: needed when device is in dark mode. Remove once react native paper fixes dark mode colors
            ios_backgroundColor={theme.colors.onSurfaceDisabled}
          />
        )}
      />

      <ListSection
        title={t('happeningStart')}
        headerBackgroundColor={theme.colors.surfaceContainerLow}
      >
        <ListItem
          testID="post-creation-start-date-row"
          title={t('start')}
          arrowIcon={() => (
            <Text>{startDate?.toLocaleString('de-DE', DATE_FORMAT)}</Text>
          )}
          onPress={() => setStartDatePickerOpen(true)}
          backgroundColor={theme.colors.surfaceContainerLow}
        />
        {!isAllDay && (
          <ListItem
            title={t('time')}
            arrowIcon={() => (
              <Text>{startDate?.toLocaleString('de-DE', TIME_FORMAT)}</Text>
            )}
            onPress={() => setStartTimePickerOpen(true)}
            backgroundColor={theme.colors.surfaceContainerLow}
          />
        )}
      </ListSection>

      <ListSection
        title={t('happeningEnd')}
        headerBackgroundColor={theme.colors.surfaceContainerLow}
      >
        <ListItem
          testID="post-creation-end-date-row"
          title={t('end')}
          arrowIcon={() => (
            <Text>{endDate?.toLocaleString('de-DE', DATE_FORMAT)}</Text>
          )}
          onPress={() => setEndDatePickerOpen(true)}
          backgroundColor={theme.colors.surfaceContainerLow}
        />
        {!isAllDay && (
          <ListItem
            title={t('time')}
            arrowIcon={() => (
              <Text>{endDate?.toLocaleTimeString('de-DE', TIME_FORMAT)}</Text>
            )}
            onPress={() => setEndTimePickerOpen(true)}
            backgroundColor={theme.colors.surfaceContainerLow}
          />
        )}
      </ListSection>

      <DatePickerModal
        locale={locale}
        mode="single"
        label={t('start')}
        visible={startDatePickerOpen}
        startWeekOnMonday
        onDismiss={onStartDateDismiss}
        date={startDate}
        onConfirm={onStartDateConfirm}
        validRange={{ startDate: TODAY }}
      />
      <DatePickerModal
        locale={locale}
        mode="single"
        label={t('end')}
        startWeekOnMonday
        visible={endDatePickerOpen}
        onDismiss={onEndDateDismiss}
        date={endDate}
        onConfirm={onEndDateConfirm}
        validRange={{ startDate: startDate, endDate: maxEndDate }}
      />
      <PaperProvider theme={datetimePickerTheme}>
        <TimePickerModal
          locale={locale}
          label={t('start')}
          cancelLabel={t('cancel')}
          visible={startTimePickerOpen}
          onDismiss={onStartTimeDismiss}
          onConfirm={onStartTimeConfirm}
          hours={startDate?.getHours()}
          minutes={startDate?.getMinutes()}
        />
        <TimePickerModal
          locale={locale}
          label={t('end')}
          cancelLabel={t('cancel')}
          visible={endTimePickerOpen}
          onDismiss={onEndTimeDismiss}
          onConfirm={onEndTimeConfirm}
          hours={startDate?.getHours()}
          minutes={startDate?.getMinutes()}
        />
      </PaperProvider>
    </>
  );
};
